import { companyInfo } from "@/lib/config/company";
import { getDisplayPricePerDay } from "@/lib/utils";

// Types basés sur Schema.org

export interface OrganizationSchema {
  "@context": "https://schema.org";
  "@type": "Organization";
  name: string;
  url: string;
  logo: {
    "@type": "ImageObject";
    url: string;
    width?: number;
    height?: number;
  };
  description: string;
  email?: string;
  address: {
    "@type": "PostalAddress";
    streetAddress?: string;
    addressLocality: string;
    postalCode?: string;
    addressCountry: string;
  };
  contactPoint: {
    "@type": "ContactPoint";
    telephone: string;
    email?: string;
    contactType: string;
    availableLanguage: string[];
    areaServed?: string;
  };
  sameAs: string[];
}

export interface WebSiteSchema {
  "@context": "https://schema.org";
  "@type": "WebSite";
  name: string;
  url: string;
  potentialAction: {
    "@type": "SearchAction";
    target: {
      "@type": "EntryPoint";
      urlTemplate: string;
    };
    "query-input": string;
  };
}

export interface ServiceSchema {
  "@context": "https://schema.org";
  "@type": "Service";
  "@id"?: string;
  name?: string;
  description?: string;
  url?: string;
  serviceType: string;
  provider: {
    "@type": "Organization";
    name: string;
    url?: string;
  };
  areaServed:
    | {
        "@type": "Country";
        name: string;
      }
    | {
        "@type": "City";
        name: string;
      };
  availableChannel: {
    "@type": "ServiceChannel";
    serviceUrl: string;
  };
}

export interface BreadcrumbItem {
  name: string;
  item: string;
  position: number;
}

export interface BreadcrumbListSchema {
  "@context": "https://schema.org";
  "@type": "BreadcrumbList";
  itemListElement: {
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQPageSchema {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: {
    "@type": "Question";
    name: string;
    acceptedAnswer: {
      "@type": "Answer";
      text: string;
    };
  }[];
}

export interface EventSchema {
  "@context": "https://schema.org";
  "@type": "Event";
  name: string;
  startDate: string;
  endDate: string;
  eventStatus: string;
  eventAttendanceMode: string;
  location: {
    "@type": "Place";
    name: string;
    address: {
      "@type": "PostalAddress";
      addressCountry: string;
    };
  };
  organizer: {
    "@type": "Organization";
    name: string;
  };
  image?: string;
  description?: string;
}

export interface ProductSchema {
  "@context": "https://schema.org";
  "@type": "Product";
  name: string;
  image: string[];
  description: string;
  brand: {
    "@type": "Brand";
    name: string;
  };
  offers: {
    "@type": "Offer";
    url: string;
    priceCurrency: string;
    price: number;
    availability: string;
    itemCondition?: string;
  };
  aggregateRating?: {
    "@type": "AggregateRating";
    ratingValue: number;
    reviewCount: number;
  };
}

const getSchemaAvailabilityByVehicleStatus = (status?: string): string => {
  const normalizedStatus = (status || "").toLowerCase();
  if (normalizedStatus === "available") {
    return "https://schema.org/InStock";
  }
  if (normalizedStatus === "booked" || normalizedStatus === "reserved" || normalizedStatus === "pending") {
    return "https://schema.org/LimitedAvailability";
  }
  if (normalizedStatus === "maintenance" || normalizedStatus === "unavailable" || normalizedStatus === "inactive") {
    return "https://schema.org/OutOfStock";
  }
  return "https://schema.org/InStock";
};

// Helpers Generators

export const generateProductSchema = (vehicle: any): ProductSchema => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: `${vehicle.brand} ${vehicle.model}`,
  image: vehicle.images || [],
  description: vehicle.description || `Location ${vehicle.brand} ${vehicle.model} au Maroc`,
  brand: {
    "@type": "Brand",
    name: vehicle.brand
  },
  offers: {
    "@type": "Offer",
    url: `https://rakb.ma/cars/${vehicle.id}`,
    priceCurrency: "MAD",
    price: getDisplayPricePerDay(vehicle.price_per_day || 0),
    availability: getSchemaAvailabilityByVehicleStatus(vehicle.status),
    itemCondition: "https://schema.org/UsedCondition"
  },
  ...(vehicle.rating && vehicle.reviews_count > 0 && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: vehicle.rating,
      reviewCount: vehicle.reviews_count
    }
  })
});

export const RAKB_ORGANIZATION_SCHEMA_ID = 'https://rakb.ma/#organization' as const;
const LEASE_OUT_BUSINESS_FUNCTION = 'http://purl.org/goodrelations/v1#LeaseOut';

/** Date ISO (YYYY-MM-DD) — build prerender + 7 jours (Offer.priceValidUntil). */
export const getVehicleOfferPriceValidUntil = (buildDate: Date = new Date()): string => {
  const d = new Date(buildDate);
  d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString().split('T')[0] ?? '';
};

export interface VehicleRentalProductGraphInput {
  id: string;
  brand: string;
  model: string;
  canonicalUrl: string;
  images: string[];
  description: string;
  pricePerDay: number;
  status?: string;
  category?: string;
  rating?: number;
  reviewsCount?: number;
  priceValidUntil: string;
  /** Ville catalogue — active areaServed + nom produit localisé */
  areaServedName?: string | null;
}

/** Product + Offer location journalière pour graphe @graph prerender (sans @context). */
export const generateVehicleRentalProductGraphNode = (
  input: VehicleRentalProductGraphInput
): Record<string, unknown> => {
  const displayPrice = getDisplayPricePerDay(input.pricePerDay || 0);
  const priceStr = String(displayPrice);
  const productId = `${input.canonicalUrl}#product`;
  const offerId = `${input.canonicalUrl}#offer`;
  const validImages = input.images.filter(u => typeof u === 'string' && u.startsWith('http')).slice(0, 5);

  const localizedName = input.areaServedName
    ? `Location ${input.brand} ${input.model} à ${input.areaServedName}`
    : `${input.brand} ${input.model}`;

  const node: Record<string, unknown> = {
    '@type': 'Product',
    '@id': productId,
    name: localizedName,
    sku: input.id,
    model: input.model,
    additionalType: 'https://schema.org/Car',
    image: validImages.length > 0 ? validImages : ['https://rakb.ma/og-image.png?v=2025'],
    description: input.description,
    brand: {
      '@type': 'Brand',
      name: input.brand,
    },
    ...(input.category ? { category: input.category } : {}),
    offers: {
      '@type': 'Offer',
      '@id': offerId,
      url: input.canonicalUrl,
      priceCurrency: 'MAD',
      price: displayPrice,
      priceValidUntil: input.priceValidUntil,
      availability: getSchemaAvailabilityByVehicleStatus(input.status),
      itemCondition: 'https://schema.org/UsedCondition',
      businessFunction: LEASE_OUT_BUSINESS_FUNCTION,
      seller: { '@id': RAKB_ORGANIZATION_SCHEMA_ID },
      ...(input.areaServedName
        ? { areaServed: { '@type': 'City', name: input.areaServedName } }
        : {}),
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: priceStr,
        priceCurrency: 'MAD',
        unitCode: 'DAY',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: '1',
          unitCode: 'DAY',
        },
      },
    },
  };

  if (input.rating && input.reviewsCount && input.reviewsCount > 0) {
    node.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: input.rating,
      reviewCount: input.reviewsCount,
    };
  }

  return node;
};

export const generateOrganizationSchema = (): OrganizationSchema => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: companyInfo.name,
    url: companyInfo.website,
    logo: {
      "@type": "ImageObject",
      url: "https://rakb.ma/og-image.png", // Utiliser og-image.png qui existe dans public/
      width: 1200,
      height: 630
    },
    description: "Plateforme de location de voitures au Maroc. Réservez votre véhicule en ligne à Casablanca, Rabat, Marrakech, Tanger, Fès et dans toutes les villes du Maroc. Plus de 500 véhicules disponibles, tarifs compétitifs, assurance incluse.",
    email: companyInfo.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: companyInfo.address.street,
      addressLocality: companyInfo.address.city,
      postalCode: companyInfo.address.postalCode,
      addressCountry: "MA"
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: companyInfo.phone,
      email: companyInfo.email,
      contactType: "Customer Service",
      availableLanguage: ["French", "Arabic", "English"],
      areaServed: "MA" // Maroc
    },
    sameAs: [
      companyInfo.social.facebook,
      companyInfo.social.twitter,
      companyInfo.social.instagram,
      companyInfo.social.linkedin
    ]
  };
};

export const generateWebSiteSchema = (): WebSiteSchema => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "RAKB",
  url: "https://rakb.ma",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://rakb.ma/cars/search?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
});

export const generateServiceSchema = (): ServiceSchema => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://rakb.ma/#marketplace-mobility-service",
  name: "RAKB — Marketplace de location de voitures au Maroc",
  description:
    "Plateforme en ligne : comparez les offres des agences partenaires et réservez une voiture pour le Maroc.",
  url: "https://rakb.ma",
  serviceType: "Car rental",
  provider: {
    "@type": "Organization",
    name: "RAKB",
    url: "https://rakb.ma",
  },
  areaServed: {
    "@type": "Country",
    name: "Maroc",
  },
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: "https://rakb.ma",
  },
});

export const generateBreadcrumbSchema = (items: BreadcrumbItem[]): BreadcrumbListSchema => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(item => ({
    "@type": "ListItem",
    position: item.position,
    name: item.name,
    item: item.item
  }))
});

export const generateFAQSchema = (faqs: FAQItem[]): FAQPageSchema => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer
    }
  }))
});

/** AggregateRating pour E-E-A-T et rich snippets (avis) */
export interface AggregateRatingSchema {
  "@type": "AggregateRating";
  ratingValue: string;
  reviewCount: string;
  bestRating?: string;
  worstRating?: string;
}

export const generateAggregateRatingSchema = (data: {
  ratingValue: string;
  reviewCount: string;
  bestRating?: string;
  worstRating?: string;
}): AggregateRatingSchema => ({
  "@type": "AggregateRating",
  ratingValue: data.ratingValue,
  reviewCount: data.reviewCount,
  ...(data.bestRating != null && { bestRating: data.bestRating }),
  ...(data.worstRating != null && { worstRating: data.worstRating }),
});

/** Réexport pipeline JSON-LD (entité marketplace, plus CarRental). */
export type { CarRentalBusinessSchema, RakbJsonLdGraph } from "./carRentalJsonLd";
export { generateCarRentalBusinessSchema, generateRakbJsonLdGraph } from "./carRentalJsonLd";

export const generateCANEventSchema = (): EventSchema => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: "CAN 2025 - Coupe d'Afrique des Nations",
  startDate: "2025-12-21T00:00:00+01:00",
  endDate: "2026-01-18T23:59:59+01:00",
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: {
    "@type": "Place",
    name: "Maroc",
    address: {
      "@type": "PostalAddress",
      addressCountry: "MA"
    }
  },
  organizer: {
    "@type": "Organization",
    name: "CAF"
  },
  description: "La Coupe d'Afrique des Nations 2025 organisée au Maroc.",
  image: "https://rakb.ma/images/can-2025-hero.jpg" // Placeholder ou image réelle
});

export interface ItemListSchema {
  "@context": "https://schema.org";
  "@type": "ItemList";
  itemListElement: {
    "@type": "ListItem";
    position: number;
    name: string;
    url: string;
  }[];
}

export const generateItemListSchema = (items: { name: string; url: string }[]): ItemListSchema => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    url: item.url
  }))
});

/**
 * Service « page ville » : intention géographique sans prétendre à une agence physique RAKB sur place.
 * Pas d’adresse / geo / horaires de loueur local.
 */
export interface CityServiceSchema {
  "@context": "https://schema.org";
  "@type": "Service";
  "@id": string;
  name: string;
  description: string;
  url: string;
  serviceType: string;
  provider: {
    "@type": "Organization";
    name: string;
    url: string;
  };
  areaServed: {
    "@type": "City";
    name: string;
  };
  availableChannel: {
    "@type": "ServiceChannel";
    serviceUrl: string;
  };
}

export const generateCityServiceSchema = (city: {
  slug: string;
  name: string;
  region: string;
  postalCode: string;
  coordinates: { latitude: number; longitude: number };
  metaDescription?: { fr: string };
  /** URL canonique de la page (ex. /fr/location-voiture/... en prod SEO). */
  canonicalUrl?: string;
}): CityServiceSchema => {
  const pageUrl =
    city.canonicalUrl?.trim() ||
    `https://rakb.ma/location-voiture/${city.slug}`;
  const searchUrl = `https://rakb.ma/search?location=${encodeURIComponent(city.name)}`;
  const desc =
    city.metaDescription?.fr ||
    `Sur RAKB, comparez en ligne les offres des agences partenaires pour une location de voiture à ${city.name} (Maroc).`;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#city-marketplace`,
    name: `Location voiture ${city.name} — marketplace RAKB`,
    description: desc,
    url: pageUrl,
    serviceType: "Car rental",
    provider: {
      "@type": "Organization",
      name: "RAKB",
      url: "https://rakb.ma",
    },
    areaServed: {
      "@type": "City",
      name: city.name,
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: searchUrl,
    },
  };
};

// HowTo Schema for step-by-step guides
export interface HowToStep {
  position: number;
  name: string;
  text: string;
  image?: string;
}

export interface HowToSchema {
  "@context": "https://schema.org";
  "@type": "HowTo";
  name: string;
  description?: string;
  image?: string;
  step: {
    "@type": "HowToStep";
    position: number;
    name: string;
    text: string;
    image?: string;
  }[];
}

export const generateHowToSchema = (data: {
  name: string;
  description?: string;
  image?: string;
  steps: HowToStep[];
}): HowToSchema => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: data.name,
    description: data.description,
    image: data.image || "https://rakb.ma/og-image.png",
    step: data.steps.map(step => ({
      "@type": "HowToStep",
      position: step.position,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image })
    }))
  };
};

/** BlogPosting schema for a single article page (legacy + SEO blog). */
export const generateBlogPostingSchema = (data: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  authorName?: string;
}): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: data.headline,
  description: data.description,
  datePublished: data.datePublished,
  dateModified: data.dateModified ?? data.datePublished,
  author: { '@type': 'Organization', name: data.authorName ?? 'RAKB' },
  publisher: {
    '@type': 'Organization',
    name: 'RAKB',
    logo: { '@type': 'ImageObject', url: 'https://rakb.ma/og-image.png' },
  },
  mainEntityOfPage: data.url,
  ...(data.image ? { image: data.image } : {}),
});

// Blog Schema
export interface BlogSchema {
  "@context": "https://schema.org";
  "@type": "Blog";
  name: string;
  url: string;
  description?: string;
  publisher: {
    "@type": "Organization";
    name: string;
    logo: {
      "@type": "ImageObject";
      url: string;
    };
  };
  blogPost?: {
    "@type": "BlogPosting";
    headline: string;
    url: string;
    datePublished?: string;
    author: {
      "@type": "Person";
      name: string;
    };
  }[];
}

export const generateBlogSchema = (data: {
  name: string;
  url: string;
  description?: string;
  posts?: {
    headline: string;
    url: string;
    datePublished?: string;
    author: string;
  }[];
}): BlogSchema => {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: data.name,
    url: data.url,
    description: data.description,
    publisher: {
      "@type": "Organization",
      name: "RAKB",
      logo: {
        "@type": "ImageObject",
        url: "https://rakb.ma/logo.png"
      }
    },
    ...(data.posts && {
      blogPost: data.posts.map(post => ({
        "@type": "BlogPosting",
        headline: post.headline,
        url: post.url,
        ...(post.datePublished && { datePublished: post.datePublished }),
        author: {
          "@type": "Person",
          name: post.author
        }
      }))
    })
  };
};

// ContactPage Schema
export interface ContactPageSchema {
  "@context": "https://schema.org";
  "@type": "ContactPage";
  mainEntity: {
    "@type": "Organization";
    name: string;
    url: string;
    contactPoint: {
      "@type": "ContactPoint";
      telephone: string;
      contactType: string;
      email?: string;
      availableLanguage: string[];
      areaServed: string;
    }[];
    address?: {
      "@type": "PostalAddress";
      streetAddress?: string;
      addressLocality: string;
      addressCountry: string;
    };
  };
}

export const generateContactPageSchema = (data: {
  organizationName: string;
  url: string;
  phone: string;
  email?: string;
  address?: {
    street?: string;
    city: string;
    country: string;
  };
}): ContactPageSchema => {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: {
      "@type": "Organization",
      name: data.organizationName,
      url: data.url,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: data.phone,
          contactType: "Customer Service",
          ...(data.email && { email: data.email }),
          availableLanguage: ["French", "Arabic", "English"],
          areaServed: "MA"
        }
      ],
      ...(data.address && {
        address: {
          "@type": "PostalAddress",
          ...(data.address.street && { streetAddress: data.address.street }),
          addressLocality: data.address.city,
          addressCountry: data.address.country
        }
      })
    }
  };
};
